import React from 'react';
import { cn } from './cn';

/**
 * StatusPill — the canonical status indicator for bookings, payments and
 * requests. Maps a domain status string to a token-backed colour so every
 * page shows the same colour for the same state.
 *
 * Props:
 *  - status: known status key (see STATUS map) — case-insensitive
 *  - label:  optional override text (defaults to a humanised status)
 *  - dot:    show a leading status dot (default true)
 *  - size:   'sm' | 'md'
 */
const STATUS = {
  confirmed:  { label: 'Confirmed',  cls: 'bg-ga-success/15 text-ga-success border-ga-success/30',  dot: 'bg-ga-success' },
  approved:   { label: 'Approved',   cls: 'bg-ga-success/15 text-ga-success border-ga-success/30',  dot: 'bg-ga-success' },
  booked:     { label: 'Booked',     cls: 'bg-ga-success/15 text-ga-success border-ga-success/30',  dot: 'bg-ga-success' },
  paid:       { label: 'Paid',       cls: 'bg-ga-success/15 text-ga-success border-ga-success/30',  dot: 'bg-ga-success' },
  completed:  { label: 'Completed',  cls: 'bg-ga-primary-soft text-ga-primary border-ga-primary/30', dot: 'bg-ga-primary' },
  pending:    { label: 'Pending',    cls: 'bg-ga-warning/15 text-ga-warning border-ga-warning/30',  dot: 'bg-ga-warning' },
  partial:    { label: 'Partial',    cls: 'bg-ga-warning/15 text-ga-warning border-ga-warning/30',  dot: 'bg-ga-warning' },
  waitlist:   { label: 'Waitlisted', cls: 'bg-ga-info/15 text-ga-info border-ga-info/30',           dot: 'bg-ga-info' },
  requested:  { label: 'Requested',  cls: 'bg-ga-info/15 text-ga-info border-ga-info/30',           dot: 'bg-ga-info' },
  cancelled:  { label: 'Cancelled',  cls: 'bg-ga-danger/15 text-ga-danger border-ga-danger/30',     dot: 'bg-ga-danger' },
  rejected:   { label: 'Rejected',   cls: 'bg-ga-danger/15 text-ga-danger border-ga-danger/30',     dot: 'bg-ga-danger' },
  overdue:    { label: 'Overdue',    cls: 'bg-ga-danger/15 text-ga-danger border-ga-danger/30',     dot: 'bg-ga-danger' },
  neutral:    { label: 'Unknown',    cls: 'bg-ga-bg-surface-subtle text-ga-text-muted border-ga-border-subtle', dot: 'bg-ga-text-subtle' },
};

const SIZES = {
  sm: 'px-2 py-0.5 text-[11px] gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
};

export default function StatusPill({ status = 'neutral', label, dot = true, size = 'md', className, ...props }) {
  const key = String(status).toLowerCase();
  const cfg = STATUS[key] || STATUS.neutral;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-ga-full border font-semibold whitespace-nowrap',
        SIZES[size] || SIZES.md,
        cfg.cls,
        className
      )}
      {...props}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} aria-hidden />}
      {label || cfg.label}
    </span>
  );
}
